import { useMemo } from "react";
import { useSelectableMetrics } from "@/hooks/api/use-metrics-definitions";
import type { WebsitePageMetrics } from "@/hooks/api/use-website-page-metrics";
import { useWebpageColumnSelection } from "@/hooks/use-column-selection";
import { getWebpageColumns } from "@/pages/webpages/webpage-list/webpage-columns";
import {
  EntityColumnSelector,
  type SelectableMetric,
} from "./entity-column-selector";

interface WebpagesColumnSelectorProps {
  selectedMetrics?: Array<keyof WebsitePageMetrics>;
  onSelectMetrics?: (metrics: Array<keyof WebsitePageMetrics>) => void;
  showMetricsSection?: boolean;
  defaultMetrics?: Array<keyof WebsitePageMetrics>;
}

/**
 * Component for managing webpage column selection including both core and custom fields,
 * and optionally metrics. Displays a popover with checkboxes for selecting which columns to display.
 */
export function WebpagesColumnSelector({
  selectedMetrics = [],
  onSelectMetrics,
  showMetricsSection = false,
  defaultMetrics = [],
}: WebpagesColumnSelectorProps) {
  // Get the core columns
  const coreColumns = getWebpageColumns();

  // Use the new hook that works with both core and custom columns
  const { availableColumns, isColumnSelected, toggleColumn, isLoading, error } =
    useWebpageColumnSelection(coreColumns, selectedMetrics);

  // Filter out metric columns from availableColumns since they're handled separately
  const filteredAvailableColumns = useMemo(() => {
    return availableColumns.filter((column) => column.columnType !== "metric");
  }, [availableColumns]);

  // Fetch metrics using the new hook
  const { data: fetchedMetrics } = useSelectableMetrics("webpages");

  // Use fetched metrics if available, otherwise empty array
  const availableMetrics = useMemo<SelectableMetric[]>(() => {
    if (!showMetricsSection) return [];
    return fetchedMetrics || [];
  }, [showMetricsSection, fetchedMetrics]);

  // Convert selected metrics to string array for the EntityColumnSelector
  const selectedMetricIds = useMemo(() => {
    return selectedMetrics.map((metric) => metric as string);
  }, [selectedMetrics]);

  // Handle metrics selection
  const handleSelectMetrics = (metricIds: string[]) => {
    if (onSelectMetrics) {
      onSelectMetrics(metricIds as Array<keyof WebsitePageMetrics>);
    }
  };

  return (
    <EntityColumnSelector
      availableColumns={filteredAvailableColumns}
      isColumnSelected={isColumnSelected}
      toggleColumn={toggleColumn}
      isLoading={isLoading}
      error={error}
      buttonLabel="Columns"
      noColumnsMessage="No columns available."
      errorMessage="Error loading columns."
      // Metrics props
      availableMetrics={availableMetrics}
      selectedMetrics={selectedMetricIds}
      onSelectMetrics={handleSelectMetrics}
      showMetricsSection={showMetricsSection}
      defaultMetrics={defaultMetrics}
    />
  );
}
